<template>
  <aside class="artwork-panel" :style="artworkStyle">
    <div class="media-stack">
      <div class="song-image">
        <img
          v-if="metadata?.songImage"
          :src="metadata.songImage"
          :alt="songAlt"
        />
        <div v-else class="fallback-cover" aria-hidden="true">
          <i class="fa fa-music"></i>
        </div>
      </div>

      <div class="track-copy">
        <strong :title="songTitle">{{ songTitle }}</strong>
        <p>{{ artistName }}</p>
      </div>

      <div class="artist-image" :aria-label="artistAlt" style="display: none">
        <img v-if="avatarImage" :src="avatarImage" :alt="artistAlt" />
        <i v-else class="fa fa-user" aria-hidden="true"></i>
      </div>
    </div>

    <div class="lyrics-panel">
      <div class="lyrics-heading">
        <span>LYRICS</span>
        <strong>#{{ displayIndex }}</strong>
      </div>

      <div
        v-if="displayLyrics.length"
        ref="lyricsViewport"
        class="lyrics-lines"
      >
        <div ref="lyricsTrack" class="lyrics-track">
          <p
            v-for="(line, index) in displayLyrics"
            :key="`${line.time}-${index}-${line.text}`"
            :ref="(element) => setLyricLineElement(line.index, element)"
            :class="{ active: line.index === activeLyricIndex }"
          >
            {{ line.text }}
          </p>
        </div>
      </div>

      <div v-else class="lyrics-empty">
        <strong>No lyrics loaded.</strong>
        <span>Lyrics are unavailable for this track.</span>
      </div>
    </div>
  </aside>
</template>

<script setup>
/* global defineProps */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { parseTrackFileName } from "../musicMetadata.js";

const props = defineProps({
  currentItem: {
    type: Object,
    default: null,
  },
  currentIndex: {
    type: Number,
    default: 0,
  },
  currentTime: {
    type: Number,
    default: 0,
  },
  lyrics: {
    type: String,
    default: "",
  },
  total: {
    type: Number,
    default: 0,
  },
  metadata: {
    type: Object,
    default: null,
  },
});

const displayIndex = computed(() => {
  if (!props.total) return "00";
  return String(props.currentIndex + 1).padStart(2, "0");
});

const artworkStyle = computed(() => {
  const seed = hashString(props.currentItem?.name || "local music");
  const hue = 190 + (seed % 120);
  const accent = `hsl(${hue} 78% 54%)`;

  return {
    "--art-accent": accent,
  };
});

const trackInfo = computed(() => parseTrackFileName(props.currentItem?.name));

const songTitle = computed(() =>
  props.currentItem ? trackInfo.value.title : "No track selected"
);

const artistName = computed(() => trackInfo.value.artist || "Unknown singer");

const songAlt = computed(() => `${songTitle.value} artwork`);

const artistAlt = computed(() => `${artistName.value} avatar`);

const avatarImage = computed(
  () => metadataImage("artistImage") || metadataImage("songImage")
);
const ACTIVE_LYRIC_CENTER_RATIO = 0.5;
const lyricsViewport = ref(null);
const lyricsTrack = ref(null);
const lyricLineElements = ref({});
let lyricsLayoutFrame = 0;

const lyricLines = computed(() => parseLyrics(props.lyrics));
const hasSyncedLyrics = computed(() =>
  lyricLines.value.some((line) => line.time !== null)
);

const activeLyricIndex = computed(() => {
  if (!hasSyncedLyrics.value) return -1;

  let currentLine = null;
  lyricLines.value.forEach((line) => {
    if (line.time !== null && line.time <= props.currentTime) {
      currentLine = line;
    }
  });

  return currentLine?.index ?? -1;
});

const displayLyrics = computed(() => lyricLines.value);

onMounted(() => {
  window.addEventListener("resize", scheduleLyricsLayoutUpdate, {
    passive: true,
  });
  scheduleLyricsLayoutUpdate();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", scheduleLyricsLayoutUpdate);
  cancelLyricsLayoutUpdate();
});

watch(
  displayLyrics,
  () => {
    lyricLineElements.value = {};
    scheduleLyricsLayoutUpdate({ reset: true });
  },
  { flush: "post" }
);

watch(
  () => props.currentItem?.name,
  () => {
    scheduleLyricsLayoutUpdate({ reset: true });
  },
  { flush: "post" }
);

watch(
  activeLyricIndex,
  () => {
    scheduleLyricsLayoutUpdate();
  },
  { flush: "post" }
);

function setLyricLineElement(index, element) {
  if (element) {
    lyricLineElements.value[index] = element;
    return;
  }

  delete lyricLineElements.value[index];
}

function scheduleLyricsLayoutUpdate(options = {}) {
  nextTick(() => {
    cancelLyricsLayoutUpdate();
    lyricsLayoutFrame = window.requestAnimationFrame(() => {
      lyricsLayoutFrame = 0;
      updateLyricsLayout(options);
    });
  });
}

function cancelLyricsLayoutUpdate() {
  if (!lyricsLayoutFrame) {
    return;
  }

  window.cancelAnimationFrame(lyricsLayoutFrame);
  lyricsLayoutFrame = 0;
}

function updateLyricsLayout({ reset = false } = {}) {
  const viewport = lyricsViewport.value;
  const track = lyricsTrack.value;
  const lines = displayLyrics.value
    .map((line) => lyricLineElements.value[line.index])
    .filter(Boolean);

  if (!viewport || !track || lines.length !== displayLyrics.value.length) {
    return;
  }

  track.style.paddingTop = "0px";
  track.style.paddingBottom = "0px";

  if (!hasSyncedLyrics.value) {
    if (reset) {
      viewport.scrollTop = 0;
    }
    return;
  }

  const viewportHeight = viewport.clientHeight;
  const centerLine = viewportHeight * ACTIVE_LYRIC_CENTER_RATIO;

  const targetLyricIndex = activeLyricIndex.value;
  if (targetLyricIndex < 0) {
    if (reset) {
      viewport.scrollTop = 0;
    }
    return;
  }

  const activeLineElement = lyricLineElements.value[targetLyricIndex];
  if (!activeLineElement) {
    return;
  }

  if (reset) {
    centerActiveLyric(viewport, activeLineElement, centerLine);
    return;
  }

  centerActiveLyric(viewport, activeLineElement, centerLine);
}

function centerActiveLyric(viewport, activeLineElement, centerLine) {
  const viewportRect = viewport.getBoundingClientRect();
  const activeRect = activeLineElement.getBoundingClientRect();
  const activeLineCenter =
    viewport.scrollTop +
    (activeRect.top - viewportRect.top) +
    activeRect.height / 2;
  const maxScrollTop = Math.max(
    viewport.scrollHeight - viewport.clientHeight,
    0
  );
  const nextScrollTop = Math.min(
    Math.max(activeLineCenter - centerLine, 0),
    maxScrollTop
  );

  if (Math.abs(viewport.scrollTop - nextScrollTop) < 1) {
    return;
  }

  viewport.scrollTop = nextScrollTop;
}

function hashString(value) {
  return [...value].reduce((hash, char) => {
    return (hash * 31 + char.charCodeAt(0)) >>> 0;
  }, 7);
}

function metadataImage(key) {
  return props.metadata?.[key] || "";
}

function parseLyrics(value = "") {
  return value
    .split(/\r?\n/)
    .flatMap((line, sourceIndex) =>
      parseLyricLine(line).map((lyric) => ({
        ...lyric,
        sourceIndex,
      }))
    )
    .filter((line) => line.text)
    .sort((a, b) => {
      if (a.time === null && b.time === null)
        return a.sourceIndex - b.sourceIndex;
      if (a.time === null) return 1;
      if (b.time === null) return -1;
      if (a.time === b.time) return a.sourceIndex - b.sourceIndex;
      return a.time - b.time;
    })
    .map((line, index) => ({
      time: line.time,
      text: line.text,
      index,
    }));
}

function parseLyricLine(line = "") {
  const timestamps = [
    ...line.matchAll(/\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\]/g),
  ];
  const text = line.replace(/\[[^\]]+\]/g, "").trim();

  if (!timestamps.length) {
    return [{ time: null, text }];
  }

  return timestamps.map((match) => {
    const minutes = Number(match[1]);
    const seconds = Number(match[2]);
    const fraction = Number((match[3] || "").padEnd(3, "0")) / 1000;
    return {
      time: minutes * 60 + seconds + fraction,
      text,
    };
  });
}
</script>

<style scoped lang="less">
.artwork-panel {
  --lyrics-viewport-height: 540px;
  position: relative;
  display: flex;
  min-height: 560px;
  overflow: hidden;
  flex-direction: column;
  gap: 28px;
  padding: 34px 36px;
  background: transparent;
}

.media-stack {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
}

.song-image,
.fallback-cover {
  display: grid;
  width: 96px;
  height: 96px;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.song-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fallback-cover {
  color: rgba(255, 255, 255, 0.74);
  font-size: 34px;
}

.track-copy {
  min-width: 0;
}

.track-copy span,
.lyrics-heading span {
  color: #8f8987;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
}

.track-copy strong {
  display: block;
  overflow: hidden;
  margin-top: 8px;
  color: #f7f2ee;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.12;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.track-copy p {
  margin: 8px 0 0;
  overflow: hidden;
  color: #b7afab;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artist-image {
  display: grid;
  width: 52px;
  height: 52px;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  color: var(--art-accent);
  background: rgba(255, 255, 255, 0.05);
}

.artist-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lyrics-panel {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 22px;
}

.lyrics-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.lyrics-heading strong {
  color: var(--art-accent);
  font-size: 14px;
}

.lyrics-lines {
  height: var(--lyrics-viewport-height);
  overflow-x: hidden;
  overflow-y: auto;
  flex: 0 0 auto;
  scrollbar-width: none;
}

.lyrics-track {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.lyrics-lines::-webkit-scrollbar {
  display: none;
}

.lyrics-lines p {
  margin: 0;
  color: #837b77;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
}

.lyrics-lines p.active {
  color: #f7f2ee;
}

.lyrics-empty {
  display: flex;
  height: var(--lyrics-viewport-height);
  flex: 0 0 auto;
  flex-direction: column;
  justify-content: center;
  margin-top: auto;
  margin-bottom: auto;
  color: #8f8987;
}

.lyrics-empty strong {
  margin-bottom: 8px;
  color: #f7f2ee;
  font-size: 18px;
}

.lyrics-empty span {
  font-size: 13px;
  line-height: 1.4;
}

@media (max-width: 860px) {
  .artwork-panel {
    --lyrics-viewport-height: 180px;
    min-height: 320px;
    gap: 22px;
    padding: 24px 22px;
  }

  .media-stack {
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 12px;
  }

  .song-image,
  .fallback-cover {
    width: 72px;
    height: 72px;
  }

  .artist-image {
    width: 44px;
    height: 44px;
  }

  .track-copy strong {
    font-size: 18px;
  }

  .lyrics-lines p {
    font-size: 14px;
  }

  .lyrics-track {
    gap: 10px;
  }
}
</style>
